/**
 * React Query hook for Payment Requests V2 with RDS-based pagination
 * Uses the new listPaymentRequestV2 API with improved filtering and sorting
 */

import { useQuery } from '@tanstack/react-query';
import { listPaymentRequestV2 } from 'services/billingServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Fetches payment request data with RDS-based pagination
 * API Response format: { total_count, page_count, page, limit, data: [] }
 *
 * @param {Object} params - Request parameters
 * @returns {Promise<Object>} - Response with data array and pagination metadata
 */
const fetchPaymentRequestV2 = async ({
  limit = 10,
  page = 1,
  payment_request_status = '',
  case_id = '',
  search_key = '',
  payment_date_lower = '',
  payment_date_upper = '',
  practice_area_list = [],
  sort_by = '',
  sort_order = '',
}) => {
  try {
    const response = await listPaymentRequestV2({
      payment_request_status,
      case_id,
      search_key,
      payment_date_lower,
      payment_date_upper,
      practice_area_list,
      sort_by,
      sort_order,
      limit,
      page,
    });

    if (response?.data) {
      return response.data;
    }

    // Return empty structure if no data (matching API format)
    return {
      total_count: 0,
      page_count: 0,
      page: page,
      limit: limit,
      data: [],
    };
  } catch (error) {
    console.error('Error fetching payment requests V2:', error);
    throw error;
  }
};

/**
 * Hook to fetch payment requests with RDS-based pagination using React Query
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Items per page (default: 10)
 * @param {number} options.page - Page number (default: 1)
 * @param {string} options.payment_request_status - Filter by status: SENT, PARTIAL, PAID, OVERDUE, ALL (default: 'ALL')
 * @param {string} options.case_id - Filter by case UUID
 * @param {string} options.search_key - Search keyword
 * @param {Array} options.practice_area_list - Filter by practice area IDs
 * @param {string} options.payment_date_lower - Start date filter (ISO format)
 * @param {string} options.payment_date_upper - End date filter (ISO format)
 * @param {string} options.sort_by - Sort field (default: 'payment_request_date')
 *   Allowed values: 'payment_request_date', 'payment_request_number', 'amount', 'due_date',
 *   'due_amount', 'paid_amount', 'payment_request_status', 'case_name', 'time'
 * @param {string} options.sort_order - Sort direction: 'asc' or 'desc' (default: 'desc')
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} Query result object
 * @returns {Array} returns.paymentRequests - Array of payment request objects
 * @returns {number} returns.totalPages - Total number of pages
 * @returns {number} returns.currentPage - Current page number
 * @returns {number} returns.pageSize - Items per page
 * @returns {number} returns.totalCount - Total number of records
 * @returns {boolean} returns.isLoading - Initial loading state
 * @returns {boolean} returns.isFetching - Fetching state (including background refetch)
 * @returns {boolean} returns.isError - Error state indicator
 * @returns {Object|null} returns.error - Error object if request failed
 * @returns {Function} returns.refetch - Function to manually refetch data
 *
 * @example
 * const {
 *   paymentRequests,
 *   totalPages,
 *   isLoading,
 *   refetch
 * } = usePaymentRequestListV2Query({
 *   limit: 10,
 *   page: 1,
 *   payment_request_status: 'SENT',
 *   case_id: 'case-uuid',
 *   search_key: 'PR-001',
 *   practice_area_list: ['practice-id-1'],
 *   sort_by: 'payment_request_date',
 *   sort_order: 'desc',
 * });
 */
export const usePaymentRequestListV2Query = ({
  limit = 10,
  page = 1,
  payment_request_status = '',
  case_id = '',
  search_key = '',
  payment_date_lower = '',
  payment_date_upper = '',
  practice_area_list = [],
  sort_by = '',
  sort_order = '',
  enabled = true,
} = {}) => {
  // Create stable query key based on all filter parameters including sorting
  const queryKey = queryKeys.paymentRequests.listV2(
    {
      payment_request_status,
      case_id,
      search_key,
      payment_date_lower,
      payment_date_upper,
      practice_area_list,
      sort_by,
      sort_order,
    },
    page,
    limit
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchPaymentRequestV2({
        limit,
        page,
        payment_request_status,
        case_id,
        search_key,
        payment_date_lower,
        payment_date_upper,
        practice_area_list,
        sort_by,
        sort_order,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes - serves from cache before that
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (React Query v4 uses gcTime instead of cacheTime)
    keepPreviousData: true, // Keep previous data while fetching
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: enabled, // Only refetch on mount if query is enabled
    retry: (failureCount) => {
      // Retry twice for errors
      return failureCount < 2;
    },
  });

  return {
    paymentRequests: data?.data || [],
    totalPages: data?.page_count || 0,
    currentPage: data?.page || page,
    pageSize: data?.limit || limit,
    totalCount: data?.total_count || 0,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export default usePaymentRequestListV2Query;
